import { clashKey, type ClashResult } from '@viewer/clash/lib'
import { dataCurtaPt, type ClashEnvioModulo } from './clashReconciliacao'
import {
  STATUS_BCF,
  type ClashEvento,
  type ClashTriageMap,
  type ClashTriageState,
} from './clashTriage'

// ---------------------------------------------------------------------------
// Topicos enviados para o modulo BCF ("Criar BCF") e a reconciliacao.
//
// Depois de uma corrida, os pares que mudaram de estado (resolvido,
// reaberto) tem eventos novos. Aqui calcula-se o que isso muda nos
// topicos do modulo: o estado e um comentario por topico. Nada se aplica
// sem o utilizador ver a lista e carregar em "Aplicar ao BCF".
//
// So se propoe alterar um topico que tem eventos novos: se alguem mudou
// o estado a mao no modulo, e nada aconteceu nas corridas, fica como
// esta.
// ---------------------------------------------------------------------------

export interface TopicoDoModulo {
  guid: string
  title: string
  status: string | null
}

export interface AlteracaoNoModulo {
  /** Chave do item (clashKey ou "grupo:<id>"). */
  itemKey: string
  guid: string
  titulo: string
  estadoDe: string | null
  estadoPara: string
  /** O comentario a acrescentar (um por topico). */
  comentario: string
}

const ORDEM: ClashTriageState[] = ['reaberto', 'novo', 'aceite', 'resolvido']

/** O estado de um topico: o menos avancado dos seus pares (a mesma
 *  regra da exportacao; um grupo so fecha com todos resolvidos). */
function estadoDosPares(triagem: ClashTriageMap, chaves: readonly string[]): ClashTriageState {
  let estado: ClashTriageState | null = null
  for (const k of chaves) {
    const e = triagem[k]
    const s = e?.state ?? 'novo'
    if (estado === null || ORDEM.indexOf(s) < ORDEM.indexOf(estado)) estado = s
  }
  return estado ?? 'novo'
}

/** Os eventos posteriores a `desde`, juntos por corrida e tipo. */
function eventosNovos(
  triagem: ClashTriageMap,
  chaves: readonly string[],
  desde: string,
): { texto: string; data: string }[] {
  const juntos = new Map<string, { ev: ClashEvento; n: number }>()
  for (const k of chaves) {
    for (const ev of triagem[k]?.eventos ?? []) {
      if (ev.data <= desde) continue
      const id = `${ev.data}|${ev.tipo}`
      const j = juntos.get(id)
      if (j) j.n += 1
      else juntos.set(id, { ev, n: 1 })
    }
  }
  return [...juntos.values()]
    .sort((a, b) => a.ev.data.localeCompare(b.ev.data))
    .map(({ ev, n }) => ({
      data: ev.data,
      texto: n === 1 ? ev.texto : `${n} pares do grupo. ${ev.texto}`,
    }))
}

/**
 * As alteracoes a propor aos topicos do modulo. `ate` e a data do evento
 * mais recente considerado: depois de aplicadas, passa a ser o novo
 * `aplicadoAte` do envio.
 */
export function alteracoesParaModulo(
  envio: ClashEnvioModulo,
  triagem: ClashTriageMap,
  topicos: readonly TopicoDoModulo[],
): { alteracoes: AlteracaoNoModulo[]; ate: string } {
  const porGuid = new Map(topicos.map((t) => [t.guid, t]))
  const alteracoes: AlteracaoNoModulo[] = []
  let ate = envio.aplicadoAte
  for (const [itemKey, { guid, chaves, desde }] of Object.entries(envio.topicos)) {
    const topico = porGuid.get(guid)
    // O topico foi apagado no modulo: nao se recria.
    if (!topico) continue
    const corte = desde && desde > envio.aplicadoAte ? desde : envio.aplicadoAte
    const novos = eventosNovos(triagem, chaves, corte)
    if (novos.length === 0) continue
    for (const n of novos) if (n.data > ate) ate = n.data
    alteracoes.push({
      itemKey,
      guid,
      titulo: topico.title,
      estadoDe: topico.status,
      estadoPara: STATUS_BCF[estadoDosPares(triagem, chaves)],
      comentario: novos.map((n) => n.texto).join('\n'),
    })
  }
  return { alteracoes, ate }
}

// ---------------------------------------------------------------------------
// "Acrescentar os novos ao BCF".
//
// Depois de repetir a corrida podem aparecer conflitos que o criterio de
// exportacao em uso levaria (marcados, por resolver, so com triagem) e
// que nao tem topico no BCF enviado. Cada um ganha um topico novo, como
// na exportacao (um por conflito, com o grupo indicado). Se o grupo ja
// tem topicos no BCF:
//
//   - envio por conflito: o novo leva RelatedTopics para os topicos do
//     grupo (e para os outros novos do mesmo grupo), e cada topico
//     existente do grupo ganha o novo nos seus RelatedTopics. A
//     sincronizacao junta os RelatedTopics a tres, como as etiquetas
//     (bcfSync.ts): acrescentar nao cria conflitos;
//   - envio por grupo ("um topico por grupo"): o novo nao entra no topico
//     do grupo (o viewpoint e a descricao do grupo ficavam por refazer);
//     fica com topico proprio, ligado ao do grupo nos dois sentidos, e o
//     topico do grupo recebe um comentario com os novos.
// ---------------------------------------------------------------------------

/** As chaves que ja tem topico no BCF enviado. */
export function chavesEnviadas(envio: ClashEnvioModulo): Set<string> {
  const out = new Set<string>()
  for (const t of Object.values(envio.topicos)) for (const k of t.chaves) out.add(k)
  return out
}

/** Os conflitos do criterio em uso que nao tem topico no BCF enviado. */
export function conflitosNovosParaModulo(
  candidatos: readonly ClashResult[],
  envio: ClashEnvioModulo,
): ClashResult[] {
  const enviadas = chavesEnviadas(envio)
  return candidatos.filter((r) => !enviadas.has(clashKey(r)))
}

/** Um topico novo a criar: a chave do conflito, o Guid escolhido e o
 *  grupo indicado. */
export interface NovoTopico {
  key: string
  guid: string
  grupo: string | null
  titulo: string
}

export interface RelacoesDosNovos {
  /** RelatedTopics de cada novo, pela ordem dada. */
  relacionados: string[][]
  /** Guid de um topico existente para os novos a acrescentar aos seus
   *  RelatedTopics (a ligacao de volta). */
  deVolta: Map<string, string[]>
  /** Guid do topico de grupo existente para os titulos dos novos do
   *  grupo (o comentario). */
  comentarios: Map<string, string[]>
}

/** Os topicos do grupo no BCF enviado: os de conflito (pela triagem
 *  actual) e o topico de grupo, quando o envio foi por grupo. Ficam de
 *  fora os que ja nao existem no modulo. */
function topicosDoGrupoNoEnvio(
  envio: ClashEnvioModulo,
  triagem: ClashTriageMap,
  existentes: ReadonlySet<string>,
): Map<string, { conflitos: string[]; doGrupo: string | null }> {
  const out = new Map<string, { conflitos: string[]; doGrupo: string | null }>()
  const de = (g: string) => {
    let e = out.get(g)
    if (!e) {
      e = { conflitos: [], doGrupo: null }
      out.set(g, e)
    }
    return e
  }
  for (const [itemKey, t] of Object.entries(envio.topicos)) {
    if (!existentes.has(t.guid)) continue
    if (itemKey.startsWith('grupo:')) {
      de(itemKey.slice('grupo:'.length)).doGrupo = t.guid
      continue
    }
    const g = triagem[itemKey]?.group
    if (g) de(g).conflitos.push(t.guid)
  }
  return out
}

/**
 * As ligacoes dos topicos novos aos que ja estao no BCF: os RelatedTopics
 * de cada novo, a ligacao de volta nos existentes e o comentario nos
 * topicos de grupo. `existentes` sao os Guids dos topicos que o BCF ainda
 * tem (um topico apagado no modulo nao se liga).
 */
export function relacoesDosNovos(
  novos: readonly NovoTopico[],
  envio: ClashEnvioModulo,
  triagem: ClashTriageMap,
  existentes: ReadonlySet<string>,
): RelacoesDosNovos {
  const doEnvio = topicosDoGrupoNoEnvio(envio, triagem, existentes)
  const novosPorGrupo = new Map<string, string[]>()
  for (const n of novos) {
    if (!n.grupo) continue
    const l = novosPorGrupo.get(n.grupo) ?? []
    l.push(n.guid)
    novosPorGrupo.set(n.grupo, l)
  }
  const deVolta = new Map<string, string[]>()
  const comentarios = new Map<string, string[]>()
  const relacionados = novos.map((n) => {
    if (!n.grupo) return []
    const g = doEnvio.get(n.grupo)
    const antigos = [...(g?.conflitos ?? []), ...(g?.doGrupo ? [g.doGrupo] : [])]
    for (const a of antigos) deVolta.set(a, [...(deVolta.get(a) ?? []), n.guid])
    if (g?.doGrupo) comentarios.set(g.doGrupo, [...(comentarios.get(g.doGrupo) ?? []), n.titulo])
    const irmaos = (novosPorGrupo.get(n.grupo) ?? []).filter((x) => x !== n.guid)
    return [...new Set([...antigos, ...irmaos])]
  })
  return { relacionados, deVolta, comentarios }
}

/** O comentario no topico de um grupo que recebeu conflitos novos. */
export function comentarioDosNovosNoGrupo(titulos: readonly string[], dataIso: string): string {
  const n = titulos.length
  return (
    `${n} conflito${n === 1 ? '' : 's'} novo${n === 1 ? '' : 's'} deste grupo acrescentado${n === 1 ? '' : 's'} `
    + `ao BCF em ${dataCurtaPt(dataIso)}, com tópico próprio (tópicos relacionados):\n`
    + titulos.map((t) => `- ${t}`).join('\n')
  )
}

/** O envio com os topicos novos registados (cada um so com o seu
 *  conflito, e com `desde` para nao repetir eventos ja nos comentarios). */
export function envioComNovos(
  envio: ClashEnvioModulo,
  criados: readonly { key: string; guid: string }[],
  desde: string,
): ClashEnvioModulo {
  const topicos = { ...envio.topicos }
  for (const c of criados) topicos[c.key] = { guid: c.guid, chaves: [c.key], desde }
  return { ...envio, topicos }
}

/** O envio com as chaves migradas por GlobalId (outra revisao do IFC):
 *  cada topico continua a seguir os seus conflitos. */
export function migrarChavesDoEnvio(
  envio: ClashEnvioModulo | null,
  migradas: Readonly<Record<string, string>>,
): ClashEnvioModulo | null {
  if (!envio || Object.keys(migradas).length === 0) return envio
  const topicos: ClashEnvioModulo['topicos'] = {}
  for (const [itemKey, t] of Object.entries(envio.topicos)) {
    topicos[migradas[itemKey] ?? itemKey] = { ...t, chaves: t.chaves.map((k) => migradas[k] ?? k) }
  }
  return { ...envio, topicos }
}
